import * as XLSX from "xlsx";
import { getRawMaterialPurchasesExport } from "../../services/RawMaterials/RawMaterialService";
import {
  RawMaterialPurchaseFilterExportDTO,
  RawMaterialPurchaseListDTO,
} from "../../models/RawMaterials/RawMaterial-model";

const parseNumber = (val: any): number => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = parseFloat(val.replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const getFirstNumericValue = (item: any, keys: string[]): number | null => {
  for (const key of keys) {
    const rawValue = item?.[key];
    if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
      return parseNumber(rawValue);
    }
  }
  return null;
};

const rupee = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatDate = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return "";
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getUnitPrice = (item: RawMaterialPurchaseListDTO): number => {
  const explicitUnitPrice = getFirstNumericValue(item, [
    "unitPrice",
    "UnitPrice",
    "invoiceUnitPrice",
    "InvoiceUnitPrice",
  ]);
  if (explicitUnitPrice !== null) return explicitUnitPrice;

  const invoicedQuantity =
    getFirstNumericValue(item, [
      "invoicedQuantity",
      "quantity",
      "quantityWithUnit",
      "Quantity",
      "InvoicedQuantity",
    ]) ?? 0;

  const baseAmount =
    getFirstNumericValue(item, [
      "basicAmount",
      "BasicAmount",
      "amount",
      "Amount",
    ]) ?? 0;

  if (invoicedQuantity <= 0) return 0;
  return baseAmount / invoicedQuantity;
};

const formatQuantity = (item: RawMaterialPurchaseListDTO): string => {
  if (item.quantityWithUnit) return item.quantityWithUnit;
  if (item.invoicedQuantity !== null && item.invoicedQuantity !== undefined) {
    return `${item.invoicedQuantity} Kgs`;
  }
  return "0 Kgs";
};

const rawMaterialPurchaseExportData = (rawMaterialList: RawMaterialPurchaseListDTO[]) => {
  return rawMaterialList.map((item) => ({
    "Purchase Date": formatDate(item.purchaseDate),
    "Raw Material": item.rawMaterialName || "-",
    Category: item.categoryName || "-",
    "Invoice No.": item.invoiceId || "-",
    Vendor: item.vendorName || "-",
    "Invoiced Qty": formatQuantity(item),
    "Unit": item.posAlias || "-",
    "Base Price": rupee.format(parseNumber(item.basicAmount)),
    "Unit Price": rupee.format(getUnitPrice(item)),
    "Tax Amount": rupee.format(parseNumber(item.taxAmount)),
    "Total Amount": rupee.format(parseNumber(item.totalAmount)),
    "Purchased By": item.purchasedBy || item.createdByName || item.createdbyName || "-",
  }));
};

export const rawMaterialPurchaseExport = async (
  filter: RawMaterialPurchaseFilterExportDTO
) => {
  try {
    const data = await getRawMaterialPurchasesExport(filter);
    let rawMaterialList = data.rawMaterialPurchases || [];

    if (!rawMaterialList.length) {
      console.warn("No data available for export.");
      return;
    }

    rawMaterialList = [...rawMaterialList].sort((a, b) => {
      const dateA = new Date(a.createdDate || 0).getTime();
      const dateB = new Date(b.createdDate || 0).getTime();
      return dateB - dateA;
    });

    const exportData = rawMaterialPurchaseExportData(rawMaterialList);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    ws["!cols"] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Raw Material Purchases");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `RawMaterialPurchases_${dateStr}.xlsx`);
  } catch (error) {
    console.error("Error exporting raw material purchases:", error);
    throw error;
  }
};

